export const MODES = ["listen", "echo", "read", "finish"] as const;
export type Mode = (typeof MODES)[number];

export const MODE_LABELS: Record<Mode, string> = {
  listen: "Listen",
  echo: "Echo",
  read: "Read",
  finish: "Finish",
};

export const isSilent = (mode: Mode) => mode === "read" || mode === "finish";

export const stageRate = (mode: Mode, rate: number) =>
  mode === "finish" ? Math.round(Math.min(rate * 1.15, 1.5) * 100) / 100 : rate;

export const ECHO_REVEAL_MS = 800;

export const SPEEDS = [0.75, 0.85, 1, 1.15, 1.25] as const;

/** Echo gap = chunk length × this factor + 400ms. */
export const ECHO_FACTOR = 1.5;

export const CHUNK_FONT =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

export const ECHO_GUARD_MS = 40;

export const CLIP_AHEAD = 2;

export const clipEnd = (k: number, count: number) => Math.min(k + CLIP_AHEAD, count - 1);
export const MIN_FONT_PX = 20;
export const MAX_FONT_PX = 56;

type Span = { fromMs: number; toMs: number };

export function segmentAt(segments: readonly Span[], tMs: number) {
  let lo = 0;
  let hi = segments.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (segments[mid].fromMs <= tMs) lo = mid;
    else hi = mid - 1;
  }
  return hi;
}

export const echoGapMs = (seg: Span, rate: number) =>
  ((seg.toMs - seg.fromMs) / rate) * ECHO_FACTOR + 400;

export function frame(
  segments: readonly Span[],
  tMs: number,
  s: { cur: number; mode: Mode; echoed: number },
): { idx: number; pause: "echo" | null } {
  if (
    s.mode === "echo" &&
    s.cur >= 0 &&
    s.cur !== s.echoed &&
    tMs >= segments[s.cur].toMs - ECHO_GUARD_MS
  )
    return { idx: s.cur, pause: "echo" };
  return { idx: segmentAt(segments, tMs), pause: null };
}

export function fitFontSize(maxWidthAtBase: number, stageWidth: number, base = 40) {
  const fit = maxWidthAtBase > 0 ? (base * stageWidth * 0.9) / maxWidthAtBase : MAX_FONT_PX;
  return Math.min(MAX_FONT_PX, Math.max(MIN_FONT_PX, fit));
}

export function stepRate(rate: number, dir: 1 | -1) {
  const at = SPEEDS.reduce(
    (best, s, i) => (Math.abs(s - rate) < Math.abs(SPEEDS[best] - rate) ? i : best),
    0,
  );
  return SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, at + dir))];
}

/**
 * What follows a finished pass. One passage runs Listen → Echo → Read-aloud,
 * then moves on to the next passage (or back to Listen when looping).
 */
export type Step = { kind: "stage"; mode: Mode } | { kind: "next" } | { kind: "loop" } | { kind: "done" };

export function nextStep(mode: Mode, loop: boolean, hasNext: boolean): Step {
  const k = MODES.indexOf(mode);
  if (k < MODES.length - 1) return { kind: "stage", mode: MODES[k + 1] };
  if (loop) return { kind: "loop" };
  return hasNext ? { kind: "next" } : { kind: "done" };
}

export const tapZone = (fraction: number) =>
  fraction < 1 / 3 ? "prev" : fraction > 2 / 3 ? "next" : "toggle";

const KEYS = {
  " ": "toggle",
  ArrowLeft: "prev",
  ArrowRight: "next",
  ArrowUp: "faster",
  ArrowDown: "slower",
  "1": "listen",
  "2": "echo",
  "3": "read",
  "4": "finish",
  l: "loop",
  L: "loop",
  m: "mic",
  M: "mic",
  Escape: "close",
} as const;

const SHIFT_KEYS = {
  ArrowLeft: "prevPassage",
  ArrowRight: "nextPassage",
} as const;

export type PlayerAction =
  | (typeof KEYS)[keyof typeof KEYS]
  | (typeof SHIFT_KEYS)[keyof typeof SHIFT_KEYS];

export function keyAction(key: string, shift = false): PlayerAction | null {
  if (shift && Object.hasOwn(SHIFT_KEYS, key)) return SHIFT_KEYS[key as keyof typeof SHIFT_KEYS];
  return Object.hasOwn(KEYS, key) ? KEYS[key as keyof typeof KEYS] : null;
}

/**
 * Where to start a collection: the first unplayed passage, otherwise the one
 * after the most recently played (wrapping to the first).
 */
export function resumeTarget<T extends { id: string; playCount: number; lastPlayedAt: number | null }>(
  passages: readonly T[],
): string | null {
  if (!passages.length) return null;
  const fresh = passages.find((p) => p.playCount === 0);
  if (fresh) return fresh.id;
  const last = passages.reduce((k, p, i) => ((p.lastPlayedAt ?? 0) > (passages[k].lastPlayedAt ?? 0) ? i : k), 0);
  return passages[(last + 1) % passages.length].id;
}

/**
 * Where "続きから" goes. Plays are logged per pass, so a passage is finished
 * only after its Read-aloud pass.
 */
export const resumeFromHistory = (h: { passageId: string; nextPassageId: string | null; mode: Mode }) =>
  h.mode === MODES[MODES.length - 1] ? (h.nextPassageId ?? h.passageId) : h.passageId;

export function formatMs(ms: number | null) {
  if (ms === null) return "—";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
